import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import UIBox from './ui/UIBox';
import UIText from './ui/UIText';

type ProgressBarSize = number | string;
type ProgressPercentMode = 'added' | 'learned';

interface IProgressBarProps {
  addedValue?: number;
  currentValue?: number;
  hideEmpty?: boolean;
  learnedValue?: number;
  maxWidth?: ProgressBarSize;
  minWidth?: ProgressBarSize;
  percent?: number;
  percentWidth?: ProgressBarSize;
  valueWidth?: ProgressBarSize;
  totalValue?: number;
  width?: ProgressBarSize;
}

const DEFAULT_PROGRESS_BAR_WIDTH = 220;
const DEFAULT_PROGRESS_PERCENT_WIDTH = 48;
const DEFAULT_PROGRESS_VALUE_WIDTH = 96;
const PROGRESS_BAR_GAP = 8;

const formatPercent = (value: number) => {
  if (value > 0 && value < 1) {
    return '<1%';
  }

  return `${Math.round(value)}%`;
};

const ProgressBar: React.FC<IProgressBarProps> = ({
  addedValue,
  currentValue = 0,
  learnedValue,
  maxWidth = DEFAULT_PROGRESS_BAR_WIDTH,
  minWidth = DEFAULT_PROGRESS_BAR_WIDTH,
  percentWidth = DEFAULT_PROGRESS_PERCENT_WIDTH,
  totalValue,
  valueWidth = DEFAULT_PROGRESS_VALUE_WIDTH,
  width = DEFAULT_PROGRESS_BAR_WIDTH,
}) => {
  const theme = useTheme();
  const total = totalValue ?? 100;
  const learned = learnedValue ?? currentValue;
  const added = addedValue ?? currentValue;
  const addedNotLearned = Math.max(added - learned, 0);
  const learnedFillPercent = total ? Math.min((learned * 100) / total, 100) : 0;
  const addedFillPercent = total
    ? Math.min((addedNotLearned * 100) / total, 100 - learnedFillPercent)
    : 0;
  const learnedPercent = total ? Math.min((learned * 100) / total, 100) : 0;
  const addedPercent = total ? Math.min((added * 100) / total, 100) : 0;
  const defaultPercentMode: ProgressPercentMode =
    learned > 0 ? 'learned' : 'added';
  const [percentMode, setPercentMode] =
    useState<ProgressPercentMode>(defaultPercentMode);
  const learnedColor = theme.palette.primary.vocabularyActiveSpeech;
  const selectedPercent =
    percentMode === 'learned' ? learnedPercent : addedPercent;
  const selectedPercentColor =
    percentMode === 'learned' ? learnedColor : theme.palette.primary.main;
  const handlePercentModeClick =
    (mode: ProgressPercentMode) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setPercentMode(mode);
    };

  useEffect(() => {
    setPercentMode(defaultPercentMode);
  }, [defaultPercentMode]);

  if (!total || added === 0) {
    return null;
  }

  return (
    <UIBox
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
      }}
    >
      <UIBox
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: `${PROGRESS_BAR_GAP}px`,
          justifyContent: 'flex-end',
        }}
      >
        <UIText
          color={'text.primary'}
          variant={'subtitle2'}
          fw={700}
          sx={{
            color: selectedPercentColor,
            textAlign: 'right',
            whiteSpace: 'nowrap',
            width: percentWidth,
          }}
        >
          {formatPercent(selectedPercent)}
        </UIText>
        <UIBox
          sx={{
            alignItems: 'center',
            borderRadius: 5,
            display: 'flex',
            height: 10,
            maxWidth,
            minWidth,
            overflow: 'hidden',
            width,
          }}
        >
          {learnedFillPercent > 0 && (
            <UIBox
              height={'100%'}
              width={`${learnedFillPercent}%`}
              sx={{ backgroundColor: learnedColor }}
            />
          )}
          {addedFillPercent > 0 && (
            <UIBox
              height={'100%'}
              width={`${addedFillPercent}%`}
              sx={{ backgroundColor: 'primary.main' }}
            />
          )}
          <UIBox
            flex={1}
            height={'100%'}
            sx={{ backgroundColor: 'primary.purple' }}
          />
        </UIBox>
        <UIText
          color={'text.primary'}
          variant={'subtitle2'}
          fw={700}
          sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: valueWidth }}
        >
          <UIBox
            component={'button'}
            onClick={handlePercentModeClick('learned')}
            sx={{
              background: 'transparent',
              border: 0,
              color: learnedColor,
              cursor: 'pointer',
              font: 'inherit',
              fontWeight: 'inherit',
              p: 0,
            }}
          >
            {learned}
          </UIBox>
          {' / '}
          <UIBox
            component={'button'}
            onClick={handlePercentModeClick('added')}
            sx={{
              background: 'transparent',
              border: 0,
              color: 'primary.main',
              cursor: 'pointer',
              font: 'inherit',
              fontWeight: 'inherit',
              p: 0,
            }}
          >
            {added}
          </UIBox>
        </UIText>
      </UIBox>
    </UIBox>
  );
};

export default ProgressBar;
